/**
 * Export a document to various formats
 */

import * as jsonSchema from "./json-schema";
import * as project from "./project";
import { FreigabeStatus, SchemaElementArt, type xdf3 } from "./xdatenfelder";
import { DEFAULT_DOCUMENT_PROFILE } from "./xdatenfelder";
import { serializeMessage } from "./xdatenfelder/v3/serialize";

export function exportAsJsonSchema(editorDoc: project.Document) {
  // TODO: Implement this for XDF3 Group ans Fields
  if (editorDoc.type !== "schema") {
    throw new Error("Only schema documents can be exported as JSON Schema.");
  }

  const schema = jsonSchema.fromDocument(editorDoc);
  const content = JSON.stringify(schema);

  downloadDocument(schema.$id, content, "application/json");
}

export function exportAsXdf3(editorDoc: project.Document) {
  const message = convertToXdf3(editorDoc);
  const content = serializeMessage(message);

  downloadDocument(
    `${message.schema.id}V${message.schema.version}.xdf3.xml`,
    content,
    "application/xml",
  );
}

function downloadDocument(filename: string, content: string, mimeType: string) {
  // Create a temporary link, add the file content as a data URL, and click the link
  // via javascript.
  const element = window.document.createElement("a");
  element.classList.add("d-none");
  element.href = window.URL.createObjectURL(
    new Blob([content], { type: mimeType }),
  );
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

export function convertToXdf3(editorDoc: project.Document): xdf3.SchemaMessage {
  project.assert(editorDoc.type === "schema");
  const now = new Date();

  let version = undefined;
  if (editorDoc.schema.type === "version") {
    version = editorDoc.schema.fimVersion;
  }

  // TODO: implement missing properties
  return {
    header: {
      nachrichtId: "xdf.x-gov.de:beta-xdf3-export",
      erstellungsZeitpunkt: now,
    },
    schema: {
      identifier: `${editorDoc.schema.fimId}:${version}`,
      id: editorDoc.schema.fimId,
      version,
      name: editorDoc.schema.basicData.name,
      description: undefined,
      definition: undefined,
      releaseState: FreigabeStatus.Entwurf,
      stateSetAt: undefined,
      stateSetBy: undefined,
      validSince: undefined,
      validUntil: undefined,
      publishedAt: now,
      lastChangedAt: now,
      normReferences: [],
      relations: [],
      keywords: [],
      label: editorDoc.schema.basicData.name,
      help: undefined,
      ableitungsmodifikationenStruktur:
        editorDoc.schema.ableitungsModifikationenStrutkur,
      ableitungsmodifikationenRepraesentation:
        editorDoc.schema.ableitungsModifikationenRepraesentation,
      dokumentsteckbrief: { id: DEFAULT_DOCUMENT_PROFILE },
      rules: [],
      children: createChildRefs(editorDoc.schema.children),
    },
    gruppen: createGroups(editorDoc.groups, now),
    felder: createFields(editorDoc.fields, now),
    regeln: {},
  };
}

function createGroups(
  groups: Record<string, project.Group>,
  now: Date,
): Record<string, xdf3.DataGroup> {
  const xdf3Groups: Record<string, xdf3.DataGroup> = {};

  for (const group of Object.values(groups)) {
    let fimVersion = undefined;
    if (group.type === "version") {
      fimVersion = group.fimVersion;
    }

    // TODO: implement missing properties
    xdf3Groups[group.fimId] = {
      identifier: `${group.fimId}:${fimVersion}`,
      id: group.fimId,
      version: fimVersion,
      name: group.basicData.name,
      description: undefined,
      definition: undefined,
      releaseState: FreigabeStatus.Entwurf,
      stateSetAt: undefined,
      stateSetBy: undefined,
      validSince: undefined,
      validUntil: undefined,
      publishedAt: now,
      lastChangedAt: now,
      normReferences: [],
      relations: [],
      keywords: [],

      inputLabel: group.basicData.name,
      elementType: SchemaElementArt.Rechtsnormgebunden,

      rules: [],
      children: createChildRefs(group.children),
    };
  }

  return xdf3Groups;
}

function createFields(
  fields: Record<string, project.Field>,
  now: Date,
): Record<string, xdf3.DataField> {
  const xdf3Fields: Record<string, xdf3.DataField> = {};

  for (const field of Object.values(fields)) {
    let fimVersion = undefined;
    if (field.type === "version") {
      fimVersion = field.fimVersion;
    }
    // TODO: implement missing properties
    xdf3Fields[field.fimId] = {
      identifier: `${field.fimId}:${fimVersion}`,
      id: field.fimId,
      version: fimVersion,
      name: field.basicData.name,
      description: undefined,
      definition: undefined,
      releaseState: FreigabeStatus.Entwurf,
      stateSetAt: undefined,
      stateSetBy: undefined,
      validSince: undefined,
      validUntil: undefined,
      publishedAt: now,
      lastChangedAt: now,
      normReferences: [],
      relations: [],
      keywords: [],

      inputLabel: field.basicData.name,
      elementType: SchemaElementArt.Rechtsnormgebunden,

      inputType: field.inputType,
      dataType: field.dataType,
      fillType: "keine",
      rules: [],
      constraints: {},
      mediaTypes: [],
      values: field.options.map(([code, name]) => ({
        code,
        name,
        // TODO: implement
        help: undefined,
      })),
    };
  }

  return xdf3Fields;
}

function createChildRefs(children: project.Child[]): xdf3.ChildRef[] {
  return children.map((child) => {
    switch (child.type) {
      case "group":
        return {
          type: "dataGroup",
          element: {
            id: child.element.fimId,
            version: child.element.fimVersion,
          },
          anzahl: child.cardinality,
          // TODO: implement
          normReferences: [],
        };
      case "field":
        return {
          type: "dataField",
          element: {
            id: child.element.fimId,
            version: child.element.fimVersion,
          },
          anzahl: child.cardinality,
          // TODO: implement
          normReferences: [],
        };
    }
  });
}
